// Exam Day Worksheet PDF generator.
// Produces ONE page per condition. Each page has a patient-facing top half
// (today's symptoms checklist, severity scale, meds, questions, what's changed)
// and a doctor-facing bottom half (vitals box, condition-specific functional
// checkpoints, signature box). Content scales to the exam type:
//   - cp:      VA Compensation & Pension exam (focus on rating-schedule criteria)
//   - ime:     Independent Medical Examiner (focus on nexus opinion factors)
//   - routine: Civilian / VA routine visit (focus on treatment + tracking)
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const PAGE_W = 612;
const PAGE_H = 792;
const M = 40;
const C_TEXT = rgb(0.06, 0.09, 0.16);
const C_MUTED = rgb(0.39, 0.45, 0.55);
const C_BRAND = rgb(0.11, 0.30, 0.85);
const C_BRAND_SOFT = rgb(0.93, 0.96, 1.0);
const C_BG_SOFT = rgb(0.95, 0.97, 0.99);
const C_BORDER = rgb(0.78, 0.82, 0.88);
const C_RULE = rgb(0.55, 0.60, 0.70);

const EXAM_META = {
  cp:      { label: "VA C&P EXAM — DAY-OF WORKSHEET",          tagline: "Take this to your Compensation & Pension exam.", color: rgb(0.69, 0.24, 0.12) },
  ime:     { label: "IME EXAM — DAY-OF WORKSHEET",             tagline: "Take this to your Independent Medical Examiner appointment.", color: rgb(0.11, 0.30, 0.85) },
  routine: { label: "ROUTINE MEDICAL VISIT — DAY-OF WORKSHEET", tagline: "Take this to your routine medical appointment.", color: rgb(0.04, 0.50, 0.27) },
};

function sanitize(t) {
  return String(t ?? "")
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u2022\u00B7]/g, "*")
    .replace(/\u00A0/g, " ")
    .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, "");
}

function fmtDate(d) {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Pull a curated symptom checklist + functional checkpoints for a given condition.
 * Falls back to a generic set if we don't have a specific map for the code/system.
 */
function getConditionContent(issue) {
  const code = String(issue?.code || "").trim();
  const system = (issue?.system || "").toLowerCase();
  // Per-code specifics (most clinically-load-bearing first)
  const MAP = {
    "9411": { // PTSD
      symptoms: ["Intrusive memories / flashbacks", "Nightmares", "Hypervigilance", "Avoidance of reminders", "Anger / irritability", "Sleep impairment", "Concentration problems", "Emotional numbing"],
      checkpoints: ["Occupational and social impairment level", "Suicidal ideation screen (PHQ-9 #9)", "Substance use screen", "Mental Status Exam findings", "GAF / WHODAS observations"],
    },
    "9434": { // Major depressive disorder
      symptoms: ["Persistent low mood", "Loss of interest / pleasure", "Sleep disturbance", "Fatigue / low energy", "Concentration problems", "Appetite changes", "Hopelessness", "Suicidal thoughts"],
      checkpoints: ["PHQ-9 score", "Functional impairment in work / family", "Treatment response history", "Risk-of-harm assessment"],
    },
    "6260": { // Tinnitus
      symptoms: ["Constant ringing / buzzing", "Volume varies during the day", "Worse in quiet environments", "Affects sleep", "Affects concentration", "Triggered by certain noises"],
      checkpoints: ["Subjective tinnitus severity", "Impact on sleep / concentration (validated scale)", "Co-existing hearing loss verified", "Onset history & exposures"],
    },
    "6100": { // Hearing loss
      symptoms: ["Difficulty hearing speech in noise", "Asking others to repeat", "Turning volume up", "Missing parts of conversations", "Trouble on phone", "Tinnitus accompanying"],
      checkpoints: ["Pure-tone audiometry (500/1000/2000/3000/4000 Hz)", "Maryland CNC speech discrimination %", "Functional impact in workplace", "Hearing aid use and benefit"],
    },
    "6847": { // Sleep apnea
      symptoms: ["Loud snoring", "Witnessed pauses in breathing", "Wake gasping / choking", "Daytime sleepiness", "Morning headaches", "Difficulty concentrating", "Mood changes"],
      checkpoints: ["AHI / RDI from most recent sleep study", "CPAP/BiPAP compliance & response", "Daytime functional impairment", "Cardiovascular comorbidity screen"],
    },
    "8100": { // Migraine headaches
      symptoms: ["Throbbing / pulsing pain", "Light sensitivity", "Sound sensitivity", "Nausea / vomiting", "Aura before onset", "Pain limits activity", "Prostrating attacks"],
      checkpoints: ["Frequency of prostrating attacks (per month)", "Average duration of an attack", "Functional limitation during attacks", "Treatment regimen and efficacy"],
    },
    "5237": { // Lumbar strain
      symptoms: ["Lower back pain (constant)", "Pain with bending", "Pain with lifting", "Radiating pain (sciatica)", "Stiffness on waking", "Loss of range of motion", "Muscle spasms"],
      checkpoints: ["Forward flexion (degrees)", "Combined ROM thoracolumbar spine", "Painful motion / DeLuca factors", "Radiculopathy / neurologic exam", "Use of assistive devices"],
    },
    "5260": { // Knee
      symptoms: ["Pain on weight-bearing", "Pain on stairs", "Swelling", "Instability / giving way", "Crepitus / clicking", "Limited bending", "Catching / locking"],
      checkpoints: ["Flexion (degrees)", "Extension lag (degrees)", "Lachman / drawer / McMurray", "Crepitus / instability tests", "Use of brace / cane"],
    },
    "7913": { // Diabetes
      symptoms: ["Increased thirst", "Frequent urination", "Fatigue", "Blurred vision", "Numbness / tingling in feet", "Slow-healing wounds", "Episodes of hypoglycemia"],
      checkpoints: ["A1C (most recent)", "Insulin or oral agent regimen", "Diabetic complications (neuropathy/retinopathy/nephropathy)", "Restriction of activities required for control"],
    },
    "7101": { // Hypertension
      symptoms: ["Headache (occipital, AM)", "Lightheadedness", "Episodes of high BP at home", "Chest tightness with exertion"],
      checkpoints: ["3 BP readings (today, separated 10 min)", "Average home BP log", "Medication regimen", "End-organ damage screen"],
    },
    "7522": { // ED
      symptoms: ["Erection difficulty", "Inability to penetrate", "Inability to sustain", "Reduced libido", "Relationship impact"],
      checkpoints: ["Sexual function inventory score", "Medication contributors review", "Endocrine workup status"],
    },
  };
  if (MAP[code]) return MAP[code];

  // System-level fallbacks
  if (system === "musculoskeletal") {
    return {
      symptoms: ["Pain at rest", "Pain with use", "Swelling", "Reduced range of motion", "Weakness", "Loss of function during flare", "Use of assistive device"],
      checkpoints: ["Range of motion measurements", "Painful motion onset", "DeLuca factors (pain/fatigue/weakness/incoordination)", "Repeated motion observations"],
    };
  }
  if (system === "mental") {
    return {
      symptoms: ["Mood disturbance", "Sleep disturbance", "Anxiety / panic", "Concentration problems", "Irritability", "Isolation / avoidance", "Impact on work / relationships"],
      checkpoints: ["Mental status exam findings", "Occupational and social impairment", "Risk assessment", "Treatment response"],
    };
  }
  if (system === "hearing") {
    return {
      symptoms: ["Difficulty in noise", "Tinnitus", "Reduced speech understanding", "Volume needs to be elevated"],
      checkpoints: ["Audiometric thresholds", "Speech discrimination", "Functional impact"],
    };
  }
  if (system === "respiratory") {
    return {
      symptoms: ["Shortness of breath at rest", "Shortness of breath with exertion", "Cough", "Wheezing", "Sleep disturbance", "Fatigue"],
      checkpoints: ["PFTs (FEV1, FVC, FEV1/FVC)", "Use of inhalers / nebulizers", "Functional capacity (METs)", "Exacerbation frequency"],
    };
  }
  if (system === "neurological") {
    return {
      symptoms: ["Numbness / tingling", "Weakness", "Pain / burning", "Balance problems", "Loss of function during flare"],
      checkpoints: ["Sensory exam by dermatome", "Motor strength (0-5)", "Deep tendon reflexes", "Provocative tests"],
    };
  }
  // Generic
  return {
    symptoms: ["Pain", "Fatigue", "Sleep disturbance", "Loss of function", "Flare frequency"],
    checkpoints: ["Objective findings on exam", "Functional impact", "Treatment regimen response"],
  };
}

export async function createExamWorksheetPdf({
  veteranName = "Veteran",
  master526ez = {},
  appointment = null,
  conditions = [],
  prescriptions = [],
  examType = "cp", // "cp" | "ime" | "routine"
}) {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const meta = EXAM_META[examType] || EXAM_META.cp;
  const today = fmtDate(new Date());
  const activeRx = prescriptions.filter((rx) => !rx.archived);

  if (conditions.length === 0) {
    // Still produce a single sheet so the veteran has something to bring.
    conditions = [{ id: "general", claimedAs: "General Exam Worksheet", code: "", system: "" }];
  }

  conditions.forEach((issue) => {
    const page = doc.addPage([PAGE_W, PAGE_H]);
    const W = PAGE_W - M * 2;

    const draw = (txt, x, y, opts = {}) => {
      page.drawText(sanitize(txt), {
        x, y,
        size: opts.size || 10,
        font: opts.bold ? bold : font,
        color: opts.color || C_TEXT,
        maxWidth: opts.maxWidth,
      });
    };

    // ============ HEADER STRIP
    page.drawRectangle({ x: 0, y: PAGE_H - 56, width: PAGE_W, height: 56, color: meta.color });
    draw("ClaimOps VA", M, PAGE_H - 22, { size: 12, bold: true, color: rgb(1, 1, 1) });
    draw(meta.label, M, PAGE_H - 40, { size: 14, bold: true, color: rgb(1, 1, 1) });
    draw(meta.tagline, M, PAGE_H - 52, { size: 8.5, color: rgb(1, 1, 1) });
    draw(today, PAGE_W - M - 70, PAGE_H - 22, { size: 10, bold: true, color: rgb(1, 1, 1) });

    let y = PAGE_H - 76;

    // ============ PATIENT IDENTIFIERS STRIP
    page.drawRectangle({ x: M, y: y - 18, width: W, height: 22, color: C_BG_SOFT, borderColor: C_BORDER, borderWidth: 0.5 });
    draw(`Patient:  ${veteranName}`, M + 8, y - 4, { size: 10, bold: true });
    if (master526ez.dateOfBirth) draw(`DOB:  ${master526ez.dateOfBirth}`, M + 240, y - 4, { size: 9.5, color: C_MUTED });
    if (appointment?.provider) draw(`Provider:  ${appointment.provider}`, M + 360, y - 4, { size: 9.5, color: C_MUTED });
    y -= 28;

    // ============ CONDITION BANNER
    page.drawRectangle({ x: M, y: y - 26, width: W, height: 30, color: C_BRAND_SOFT, borderColor: meta.color, borderWidth: 1 });
    draw(issue.claimedAs || "Untitled condition", M + 10, y - 6, { size: 13, bold: true, color: meta.color });
    if (issue.code) draw(`DC ${issue.code}`, M + 10, y - 20, { size: 9, bold: true, color: C_MUTED });
    if (issue.theory) draw(`Theory: ${issue.theory}`, M + 90, y - 20, { size: 9, color: C_MUTED });
    if (issue.bilateralSide) draw(`Side: ${issue.bilateralSide}`, M + 320, y - 20, { size: 9, color: C_MUTED });
    y -= 38;

    // ============ SECTION HEADER HELPERS
    const sectionTitle = (txt) => {
      draw(txt, M, y, { size: 9.5, bold: true, color: meta.color });
      page.drawRectangle({ x: M, y: y - 3, width: W, height: 0.6, color: C_RULE });
      y -= 14;
    };
    const checkbox = (x, yy, size = 9) => {
      page.drawRectangle({ x, y: yy, width: size, height: size, borderColor: C_TEXT, borderWidth: 0.6, color: rgb(1, 1, 1) });
    };
    const writeLine = (x1, x2, yy) => {
      page.drawRectangle({ x: x1, y: yy, width: x2 - x1, height: 0.4, color: C_RULE });
    };

    // ===========================================================================
    // PATIENT-FACING TOP HALF
    // ===========================================================================
    const content = getConditionContent(issue);

    // -------- Today's symptoms checklist (2-column)
    sectionTitle("FOR THE PATIENT — TODAY'S SYMPTOMS  (check all that apply)");
    const colW = W / 2;
    const rowH = 14;
    const rows = Math.ceil(content.symptoms.length / 2);
    content.symptoms.forEach((s, i) => {
      const col = Math.floor(i / rows);
      const row = i % rows;
      const x = M + col * colW;
      const yy = y - row * rowH;
      checkbox(x, yy - 1);
      draw(s, x + 14, yy, { size: 9.5, maxWidth: colW - 20 });
    });
    y -= rows * rowH + 4;
    // Two "Other:" blanks
    checkbox(M, y - 1); draw("Other:", M + 14, y, { size: 9.5 }); writeLine(M + 50, M + W, y - 1); y -= rowH;
    checkbox(M, y - 1); draw("Other:", M + 14, y, { size: 9.5 }); writeLine(M + 50, M + W, y - 1); y -= rowH + 4;

    // -------- Severity scale 0-10
    sectionTitle("CURRENT SEVERITY  (circle the number that matches today's worst level)");
    const scaleY = y - 6;
    const stepX = (W - 40) / 10;
    for (let n = 0; n <= 10; n++) {
      const cx = M + 20 + n * stepX;
      page.drawCircle({ x: cx, y: scaleY, size: 8, borderColor: C_TEXT, borderWidth: 0.5, color: rgb(1, 1, 1) });
      draw(String(n), cx - (n === 10 ? 6 : 3), scaleY - 3, { size: 9, bold: true });
    }
    draw("No pain", M + 4, scaleY - 18, { size: 8, color: C_MUTED });
    draw("Mild", M + 4 + stepX * 2, scaleY - 18, { size: 8, color: C_MUTED });
    draw("Moderate", M + 4 + stepX * 5 - 18, scaleY - 18, { size: 8, color: C_MUTED });
    draw("Severe", M + 4 + stepX * 8 - 10, scaleY - 18, { size: 8, color: C_MUTED });
    draw("Worst", PAGE_W - M - 28, scaleY - 18, { size: 8, color: C_MUTED });
    y -= 36;

    // -------- Meds taken today
    sectionTitle("MEDICATIONS TAKEN TODAY  (check those you took, write dose + time)");
    if (activeRx.length === 0) {
      checkbox(M, y - 1); writeLine(M + 14, M + W, y - 1); y -= rowH;
      checkbox(M, y - 1); writeLine(M + 14, M + W, y - 1); y -= rowH;
    } else {
      activeRx.slice(0, 4).forEach((rx) => {
        checkbox(M, y - 1);
        draw(`${rx.medName}${rx.dosage ? ` - ${rx.dosage}` : ""}`, M + 14, y, { size: 9.5 });
        draw("Time taken:", M + 280, y, { size: 9, color: C_MUTED });
        writeLine(M + 330, M + W, y - 1);
        y -= rowH;
      });
      if (activeRx.length > 4) {
        checkbox(M, y - 1); draw("Other:", M + 14, y, { size: 9.5 }); writeLine(M + 50, M + W, y - 1); y -= rowH;
      }
    }
    y -= 4;

    // -------- What changed since last visit
    sectionTitle("WHAT'S CHANGED SINCE YOUR LAST VISIT?  (be specific - new symptoms, new triggers, new limits)");
    for (let i = 0; i < 3; i++) { writeLine(M, M + W, y - 1); y -= 13; }
    y -= 4;

    // -------- Questions to ask
    sectionTitle("QUESTIONS YOU WANT TO ASK THE EXAMINER  (write them now while you remember)");
    for (let i = 0; i < 3; i++) { writeLine(M, M + W, y - 1); y -= 13; }
    y -= 8;

    // ===========================================================================
    // DOCTOR-FACING BOTTOM HALF
    // ===========================================================================
    page.drawRectangle({ x: M - 6, y: y + 2, width: W + 12, height: 0.6, color: meta.color });
    y -= 6;
    draw("FOR THE EXAMINER", M, y, { size: 11, bold: true, color: meta.color });
    draw("(complete this section during today's visit)", M + 120, y, { size: 8.5, color: C_MUTED });
    y -= 14;

    // -------- Vitals box
    sectionTitle("VITALS");
    const vy = y;
    const fieldW = (W - 5 * 6) / 6;
    const fieldLabels = ["BP", "HR", "RR", "Temp", "SpO2", "Weight"];
    fieldLabels.forEach((lab, i) => {
      const fx = M + i * (fieldW + 6);
      draw(lab, fx + 4, vy, { size: 8, bold: true, color: C_MUTED });
      page.drawRectangle({ x: fx, y: vy - 18, width: fieldW, height: 16, borderColor: C_BORDER, borderWidth: 0.6, color: rgb(1, 1, 1) });
    });
    y -= 28;

    // -------- Condition-specific checkpoints
    const ctitle = examType === "cp"
      ? "RATING-SCHEDULE CHECKPOINTS  (objective findings relevant to 38 CFR Part 4)"
      : examType === "ime"
      ? "NEXUS-RELEVANT FINDINGS  (objective observations supporting the medical opinion)"
      : "EXAM FINDINGS";
    sectionTitle(ctitle);
    content.checkpoints.forEach((cp) => {
      checkbox(M, y - 1, 8);
      draw(cp, M + 13, y, { size: 9, color: C_TEXT, maxWidth: 280 });
      writeLine(M + 300, M + W, y - 1);
      y -= 13;
    });
    y -= 4;

    // -------- Free-text observations
    sectionTitle("ADDITIONAL OBSERVATIONS");
    for (let i = 0; i < 3; i++) { writeLine(M, M + W, y - 1); y -= 13; }
    y -= 6;

    // -------- Signature box at the very bottom
    const sy = 80;
    page.drawRectangle({ x: M, y: sy - 4, width: W, height: 50, borderColor: C_BORDER, borderWidth: 0.6, color: C_BG_SOFT });
    draw("Examiner signature:", M + 8, sy + 36, { size: 9, bold: true });
    writeLine(M + 110, M + 320, sy + 38);
    draw("Date:", M + 330, sy + 36, { size: 9, bold: true });
    writeLine(M + 360, M + W - 8, sy + 38);
    draw("Print name:", M + 8, sy + 18, { size: 9, bold: true });
    writeLine(M + 80, M + 320, sy + 20);
    draw("Credentials:", M + 330, sy + 18, { size: 9, bold: true });
    writeLine(M + 395, M + W - 8, sy + 20);
    draw("NPI / VA ID:", M + 8, sy, { size: 9, bold: true });
    writeLine(M + 80, M + 320, sy + 2);

    // -------- Footer
    draw("Generated by ClaimOps VA - Severity and impact descriptions are subjective self-reports.", M, 36, { size: 7.5, color: C_MUTED });
  });

  return await doc.save();
}
